import {capitalize} from './string';
import moment, {Moment} from 'moment-timezone';
import DateTimeFormat = Intl.DateTimeFormat;

export const DATE_STRING_REG_EXP = /^(\d{4})-(\d{2})-(\d{2})$/;

export const listDefaultRanges = {
    yesterday: [moment().subtract(1, 'd').toDate(), moment().subtract(1, 'd').toDate()],
    today: [moment().toDate(), moment().toDate()],
    thisWeek: [moment().startOf('week').toDate(), moment().endOf('week').toDate()],
    thisMonth: [moment().startOf('month').toDate(), moment().endOf('month').toDate()],
    thisQuarter: [
        moment().quarter(moment().quarter()).startOf('quarter').toDate(),
        moment().quarter(moment().quarter()).endOf('quarter').toDate(),
    ],
    lastWeek: [moment().startOf('week').subtract(7, 'days').toDate(), moment().endOf('week').subtract(7, 'days').toDate()],
    lastMonth: [moment().subtract(1, 'months').startOf('month').toDate(), moment().subtract(1, 'months').endOf('month').toDate()],
    lastQuarter: [
        moment()
            .quarter(moment().quarter() - 1)
            .startOf('quarter')
            .toDate(),
        moment()
            .quarter(moment().quarter() - 1)
            .endOf('quarter')
            .toDate(),
    ],
    last7Days: [moment().subtract(7, 'd').toDate(), moment().subtract(1, 'd').toDate()],
    last14Days: [
        // eslint-disable-next-line @typescript-eslint/no-magic-numbers
        moment().subtract(14, 'd').toDate(),
        moment().subtract(1, 'd').toDate(),
    ],
    last30Days: [moment().subtract(30, 'days').startOf('days').toDate(), moment().subtract(1, 'd').toDate()],
    last60Days: [moment().subtract(60, 'days').startOf('days').toDate(), moment().subtract(1, 'd').toDate()],
    last90Days: [moment().subtract(90, 'days').startOf('days').toDate(), moment().subtract(1, 'd').toDate()],
};

function now(tz) {
    const m = moment().tz(tz);
    return m ? m : moment();
}

export function getDefaultRanges(tz): Record<string, Date[]> {
    const ranges = {
        yesterday: [now(tz).subtract(1, 'd'), now(tz).subtract(1, 'd')],
        today: [now(tz), now(tz)],
        thisWeek: [now(tz).startOf('week'), now(tz).endOf('week')],
        thisMonth: [now(tz).startOf('month'), now(tz).endOf('month')],
        thisQuarter: [now(tz).quarter(now(tz).quarter()).startOf('quarter'), now(tz).quarter(now(tz).quarter()).endOf('quarter')],
        lastWeek: [now(tz).startOf('week').subtract(7, 'days'), now(tz).endOf('week').subtract(7, 'days')],
        lastMonth: [now(tz).subtract(1, 'months').startOf('month'), now(tz).subtract(1, 'months').endOf('month')],
        lastQuarter: [
            now(tz)
                .quarter(now(tz).quarter() - 1)
                .startOf('quarter'),
            now(tz)
                .quarter(now(tz).quarter() - 1)
                .endOf('quarter'),
        ],
        last7Days: [now(tz).subtract(7, 'd'), now(tz).subtract(1, 'd')],
        last14Days: [now(tz).subtract(14, 'd'), now(tz).subtract(1, 'd')],
        last28Days: [now(tz).subtract(28, 'days').startOf('days'), now(tz).subtract(1, 'd')],
        last30Days: [now(tz).subtract(30, 'days').startOf('days'), now(tz).subtract(1, 'd')],
        last60Days: [now(tz).subtract(60, 'days').startOf('days'), now(tz).subtract(1, 'd')],
        last90Days: [now(tz).subtract(90, 'days').startOf('days'), now(tz).subtract(1, 'd')],
        last180Days: [now(tz).subtract(180, 'days').startOf('days'), now(tz).subtract(1, 'd')],
        last365Days: [now(tz).subtract(365, 'days').startOf('days'), now(tz).subtract(1, 'd')],
    };

    Object.keys(ranges).forEach(k => (ranges[k] = ranges[k].map(v => new Date(v.year(), v.month(), v.date()))));

    return ranges as unknown as Record<string, Date[]>;
}

export function isValidDateString(dateString: string): boolean {
    return DATE_STRING_REG_EXP.test(dateString);
}

export function safeCreateDate(dateString: string): Date {
    let {year, month, day} = parseDateString(dateString);
    month = fixMonthValue(month - 1);
    let date = new Date(moment().toDate());
    date.setMonth(month);
    date.setFullYear(year);
    date.setUTCDate(fixDateValue(day, month, year));
    return date;
}

function fixMonthValue(value: number): number {
    const min = 0;
    const max = 11;
    if (value < min || value > max) {
        let dMin = Math.abs(value - min);
        let dMax = Math.abs(max - value);
        value = dMin < dMax ? min : max;
    }
    return value;
}

function fixDateValue(value: number, month: number, year: number): number {
    const min = 1;
    const max = new Date(year, month + 1, 0).getDate();
    if (value < min || value > max) {
        let dMin = Math.abs(value - min);
        let dMax = Math.abs(max - value);
        value = dMin < dMax ? min : max;
    }
    return value;
}

export function createMoment(date): Moment {
    // @ts-ignore
    // eslint-disable-next-line @typescript-eslint/no-magic-numbers
    return typeof date === 'number' && date > Math.pow(10, 12)
        ? //  from ms to s
          moment.utc(date)
        : moment(date);
}

export function parseDateString(dateString: string | number): {year: number; month: number; day: number} {
    if (typeof dateString === 'number') {
        // eslint-disable-next-line @typescript-eslint/no-magic-numbers
        dateString = createMoment(dateString).format('YYYY-MM-DD');
    }
    let results = DATE_STRING_REG_EXP.exec(dateString as string);
    if (results) {
        let [year, month, day] = results.slice(1, 4).map(value => parseInt(value, 10));
        return {year, month, day};
    }
    return null;
}

export function isValidateDateValue(from: string, to: string): boolean {
    const start = parseDateString(from);
    const end = parseDateString(to);
    return Boolean(start && end);
}

const MONTH_FORMAT = new Map<string, MonthFormats>();
const WEEKDAY_FORMAT = new Map<string, WeekdayFormats>();

export type MonthLabelType = 'long' | 'short' | 'narrow' | 'twoDigit' | 'numeric';
export type WeekdayLabelType = 'long' | 'short' | 'narrow';

export interface MonthLabel {
    long: string;
    short: string;
    narrow: string;
    twoDigit: string;
    numeric: string;
}

export interface MonthFormats {
    long: DateTimeFormat;
    short: DateTimeFormat;
    narrow: DateTimeFormat;
    twoDigit: DateTimeFormat;
    numeric: DateTimeFormat;
}

export interface WeekdayLabel {
    long: string;
    short: string;
    narrow: string;
}

export interface WeekdayFormats {
    long: DateTimeFormat;
    short: DateTimeFormat;
    narrow: DateTimeFormat;
}

export type Weekdays = Array<{label: WeekdayLabel; value: number}>;

export type Months = Array<{label: MonthLabel; value: number}>;

export const MONTH_COUNT = 12;
export const DEFAULT_LOCALE = 'en';
export const DEFAULT_WEEK_START = 1;

export function getYear(date: Date): number {
    return date.getUTCFullYear();
}

export function getMonth(date: Date): number {
    return date.getUTCMonth();
}

export function getDate(date: Date): number {
    return date.getUTCDate();
}

export function getDay(date: Date): number {
    return date.getUTCDay();
}

export function setDate(date: Date, dateValue: number): Date {
    date.setUTCDate(dateValue);
    return date;
}

export function getHours(date: Date): number {
    return date.getUTCHours();
}

export function getMinutes(date: Date): number {
    return date.getUTCMinutes();
}

export function createDate(year: number, month: number, date: number): Date {
    return new Date(
        [year.toString().padStart(4, '0'), (month + 1).toString().padStart(2, '0'), date.toString().padStart(2, '0')].join('-')
    );
}

export function createToday() {
    let date = new Date();
    return new Date(date.getFullYear(), date.getMonth(), date.getDate());
}

export function dateStringToParts(dateString: string) {
    if (DATE_STRING_REG_EXP.test(dateString)) {
        let [year, month, date] = dateString.split('-').map(part => Number(part));
        return {year, month, date};
    }
    return null;
}

export function dateStringToLocalDate(dateString: string, hours = 0, minutes = 0, seconds = 0, milliseconds = 0) {
    let {year, month, date} = dateStringToParts(dateString);
    return new Date(year, month - 1, date, hours, minutes, seconds, milliseconds);
}

export interface OffsetOptions {
    years: number;
    months: number;
    days: number;
    hours: number;
    minutes: number;
    seconds: number;
    milliseconds: number;
}

export function dateOffset(date: Date, {years, months, days, hours, minutes, seconds, milliseconds}: Partial<OffsetOptions> = {}): Date {
    return new Date(
        date.getFullYear() + (years || 0),
        date.getMonth() + (months || 0),
        date.getDate() + (days || 0),
        date.getHours() + (hours || 0),
        date.getMinutes() + (minutes || 0),
        date.getSeconds() + (seconds || 0),
        date.getMilliseconds() + (milliseconds || 0)
    );
}

export function compareSiblingMonths(targetMonth: number, nextMonth: number): boolean {
    const lastMonth = MONTH_COUNT - 1;
    if (targetMonth === 0 && nextMonth === lastMonth) {
        return false;
    }
    if (targetMonth === lastMonth && nextMonth === 0) {
        return true;
    }
    return targetMonth <= nextMonth;
}

export function normalizeMonthYearValues(month: number, year: number): {month: number; year: number} {
    if (month < 0) {
        month = MONTH_COUNT - 1;
        year--;
    }
    if (month >= MONTH_COUNT) {
        month = 0;
        year++;
    }
    return {
        month,
        year,
    };
}

export function createLastDayOfMonth(date: Date) {
    let targetMonth = getMonth(date);
    let targetYear = getYear(date);
    let {month, year} = normalizeMonthYearValues(targetMonth + 1, targetYear);
    let lastDay = createDate(year, month, 1);
    setDate(lastDay, 0);
    return lastDay;
}

/**
 * returns a string in format yyyy-mm-dd
 */
export function getDateString(date: Date): string {
    return date.toISOString().split('T')[0];
}

export function getMonthName(date: Date, locale: string = DEFAULT_LOCALE, type: MonthLabelType = 'long'): string {
    let monthFormats = getMonthFormats(locale);
    return capitalize(monthFormats[type].format(date));
}

export function getWeekdayName(date: Date, locale: string = DEFAULT_LOCALE, type: WeekdayLabelType = 'long'): string {
    let weekdayFormats = getWeekdayFormats(locale);
    return capitalize(weekdayFormats[type].format(date));
}

export function getMonthsList(locale: string = DEFAULT_LOCALE): Months {
    let months = new Array(MONTH_COUNT);
    for (let i = 0; i < MONTH_COUNT; i++) {
        let date = new Date(0, i, 1);
        months[i] = {
            value: i,
            label: {
                long: getMonthName(date, locale, 'long'),
                short: getMonthName(date, locale, 'short'),
                narrow: getMonthName(date, locale, 'narrow'),
                twoDigit: getMonthName(date, locale, 'twoDigit'),
                numeric: getMonthName(date, locale, 'numeric'),
            },
        };
    }
    return months;
}

function getMonthFormats(locale: string = DEFAULT_LOCALE): MonthFormats {
    if (!MONTH_FORMAT.has(locale)) {
        MONTH_FORMAT.set(locale, {
            long: new Intl.DateTimeFormat(locale, {month: 'long'}),
            short: new Intl.DateTimeFormat(locale, {month: 'short'}),
            narrow: new Intl.DateTimeFormat(locale, {month: 'narrow'}),
            twoDigit: new Intl.DateTimeFormat(locale, {month: '2-digit'}),
            numeric: new Intl.DateTimeFormat(locale, {month: 'numeric'}),
        });
    }
    return MONTH_FORMAT.get(locale);
}

function getWeekdayFormats(locale: string = DEFAULT_LOCALE): WeekdayFormats {
    if (!WEEKDAY_FORMAT.has(locale)) {
        WEEKDAY_FORMAT.set(locale, {
            long: new Intl.DateTimeFormat(locale, {weekday: 'long'}),
            short: new Intl.DateTimeFormat(locale, {weekday: 'short'}),
            narrow: new Intl.DateTimeFormat(locale, {weekday: 'narrow'}),
        });
    }
    return WEEKDAY_FORMAT.get(locale);
}

/**
 * Returns an array of localized weekdays starting from specified weekStart or according to passed locale
 */
export function getWeekDays(locale: string = DEFAULT_LOCALE, weekStart?: number): Weekdays {
    let startDay = new Date();
    if (typeof weekStart !== 'number') {
        weekStart = getWeekStart(locale);
    }
    let dayOffset = getDay(startDay) - weekStart;
    setDate(startDay, getDate(startDay) - dayOffset);
    let weekDays = new Array(7);
    for (let i = 0; i < 7; i++) {
        let value = weekStart + i;
        if (value >= 7) {
            value = value - 7;
        }
        weekDays[i] = {
            value,
            label: {
                long: getWeekdayName(startDay, locale, 'long'),
                short: getWeekdayName(startDay, locale, 'short'),
                narrow: getWeekdayName(startDay, locale, 'narrow'),
            },
        };
        startDay.setDate(startDay.getDate() + 1);
    }
    return weekDays;
}

const WEEK_START_FOR_REGIONS = new Map<number, Set<string>>([
    [
        1,
        new Set([
            'AD',
            'AI',
            'AL',
            'AM',
            'AN',
            'AT',
            'AX',
            'AZ',
            'BA',
            'BE',
            'BG',
            'BM',
            'BN',
            'BY',
            'CH',
            'CL',
            'CM',
            'CR',
            'CY',
            'CZ',
            'DE',
            'DK',
            'EC',
            'EE',
            'ES',
            'FI',
            'FJ',
            'FO',
            'FR',
            'GB',
            'GE',
            'GF',
            'GP',
            'GR',
            'HR',
            'HU',
            'IS',
            'IT',
            'KG',
            'KZ',
            'LB',
            'LI',
            'LK',
            'LT',
            'LU',
            'LV',
            'MC',
            'MD',
            'ME',
            'MK',
            'MN',
            'MQ',
            'MY',
            'NL',
            'NO',
            'PL',
            'PT',
            'RE',
            'RO',
            'RS',
            'RU',
            'SE',
            'SI',
            'SK',
            'SM',
            'TJ',
            'TM',
            'TR',
            'UA',
            'UY',
            'UZ',
            'VA',
            'VN',
            'XK',
        ]),
    ],
    [5, new Set(['BD', 'MV'])],
    [6, new Set(['AE', 'AF', 'BH', 'DJ', 'DZ', 'EG', 'IQ', 'IR', 'JO', 'KW', 'LY', 'MA', 'OM', 'QA', 'SD', 'SY'])],
    [
        0,
        new Set([
            'AG',
            'AR',
            'AS',
            'AU',
            'BR',
            'BS',
            'BT',
            'BW',
            'BZ',
            'CA',
            'CN',
            'CO',
            'DM',
            'DO',
            'ET',
            'GT',
            'GU',
            'HK',
            'HN',
            'ID',
            'IE',
            'IL',
            'IN',
            'JM',
            'JP',
            'KE',
            'KH',
            'KR',
            'LA',
            'MH',
            'MM',
            'MO',
            'MT',
            'MX',
            'MZ',
            'NI',
            'NP',
            'NZ',
            'PA',
            'PE',
            'PH',
            'PK',
            'PR',
            'PY',
            'SA',
            'SG',
            'SV',
            'TH',
            'TN',
            'TT',
            'TW',
            'UM',
            'US',
            'VE',
            'VI',
            'WS',
            'YE',
            'ZA',
            'ZW',
        ]),
    ],
]);

/**
 * Returns a number (from 0 - Sunday to 6 - Saturday) of the first day of the week according to locale. By default returns 1 (Monday).
 * Accepts locale as BCP-47 language tag. Examples: en-US, en-CA-x-ca.
 */
export function getWeekStart(locale: string = DEFAULT_LOCALE): number {
    let regionCode = locale ? getRegionCodeFromLanguageTag(locale) : null;
    if (regionCode) {
        for (let [weekStart, regions] of WEEK_START_FOR_REGIONS.entries()) {
            if (regions.has(regionCode)) {
                return weekStart;
            }
        }
    }
    return DEFAULT_WEEK_START;
}

export function getRegionCodeFromLanguageTag(languageTag: string): string {
    let regionRegExp = /([A-Z]{2})/;
    let parsingResult = regionRegExp.exec(languageTag);
    return (parsingResult && parsingResult[1]) || null;
}

export function getFormatPattern(locale: string = DEFAULT_LOCALE): {
    pattern: RegExp;
    description: Array<string>;
    placeholder: string;
    locale: string;
} {
    try {
        return _getFormatPattern(locale);
    } catch {
        return {
            pattern: new RegExp('([dA-z]{2})([^dA-z]{1})([dA-z]{2})([^dA-z]{1})([dA-z]{1,4})'),
            locale: 'en-US',
            description: ['month', 'literal', 'day', 'literal', 'year'],
            placeholder: 'mm/dd/yyyy',
        };
    }
}

function _getFormatPattern(locale: string = DEFAULT_LOCALE): {
    pattern: RegExp;
    description: Array<string>;
    placeholder: string;
    locale: string;
} {
    const regexp = /([\dA-z]+)([^\dA-z]+)?/g;
    const testData = {day: '13', month: '08', year: '1999'};
    const partTypes = Object.keys(testData);
    let testDateObject = new Date(Number(testData.year), Number(testData.month) - 1, Number(testData.day));
    let testDateString = testDateObject.toLocaleDateString(locale, {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
    });
    let placeholder = '';
    let patternParts = [];
    let description = [];
    let result = regexp.exec(testDateString);
    while (result) {
        let [, value, literal] = result;
        let type = partTypes.find(testType => value === testData[testType]);
        patternParts.push(`([\\dA-z]{${value.length === 4 ? '1,4' : value.length}})`);
        description.push(type);
        placeholder += type.charAt(0).repeat(value.length);
        if (literal) {
            patternParts.push(`([^\\dA-z]{${literal.length}})`);
            description.push('literal');
            placeholder += literal;
        }
        result = regexp.exec(testDateString);
    }
    return {
        pattern: new RegExp(patternParts.join('')),
        description,
        placeholder,
        locale,
    };
}

export function formatToParts(date?: Date, targetLocale: string = DEFAULT_LOCALE) {
    let {pattern, description, placeholder, locale} = getFormatPattern(targetLocale);
    let dateString = date
        ? date.toLocaleDateString(locale, {
              day: '2-digit',
              month: '2-digit',
              year: 'numeric',
          })
        : placeholder;
    let parseResult = pattern.exec(dateString);
    return description.map((type, index) => ({
        type,
        value: parseResult[index + 1],
    }));
}

export function getLocalizedDateString(date: Date, locale = DEFAULT_LOCALE) {
    return date.toLocaleDateString(locale, {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
    });
}

export function getQuarterNumber(date: Date): 1 | 2 | 3 | 4 {
    return Math.ceil((date.getMonth() + 1) / 3) as 1 | 2 | 3 | 4;
}

export function getQuarterStartMonth(quarterNumber: 1 | 2 | 3 | 4): number {
    return 3 * (quarterNumber - 1);
}

export function getQuarterEndMonth(quarterNumber: 1 | 2 | 3 | 4): number {
    return 2 + 3 * (quarterNumber - 1);
}

const DAYS_IN_TWO_WEEKS = 14;

export function getLastDayOfNextWeek() {
    const today = new Date();
    const dayOfWeek = today.getDay();
    const daysUntilNextSunday = DAYS_IN_TWO_WEEKS - dayOfWeek;
    const nextSunday = new Date(today);
    nextSunday.setDate(today.getDate() + daysUntilNextSunday);

    return nextSunday;
}
